import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { X, Palette, Save, ExternalLink } from "lucide-react";

/**
 * Founder-only modal to edit a single partner's white-label branding
 * (logo, hero copy, accent word, primary colour, footer). Backed by
 * `PATCH /api/partner-accounts/{pid}/brand`. The visible slug field
 * doubles as the shareable URL `/p/{slug}`.
 */
export default function PartnerBrandModal({ partner, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!partner) return;
    api.get(`/partner-accounts/${partner.id}/brand`)
      .then(({ data }) => setData({ ...data.brand, slug: data.slug }))
      .catch(e => toast.error(e?.response?.data?.detail || "Could not load brand"));
  }, [partner]);

  if (!partner) return null;

  const set = (k, v) => setData(d => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/partner-accounts/${partner.id}/brand`, data);
      toast.success(`Branding saved · share /p/${data.slug}`);
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const previewUrl = data?.slug ? `${window.location.origin}/p/${data.slug}` : "";

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="partner-brand-modal">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl my-8" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-[color:var(--aii-border)] flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-brand font-bold flex items-center gap-1.5"><Palette size={12}/> Partner white-label</div>
            <h3 className="font-display text-lg font-semibold">{partner.name}</h3>
            {previewUrl && (
              <a href={previewUrl} target="_blank" rel="noreferrer" data-testid="partner-brand-preview-link"
                className="text-xs text-brand underline decoration-dotted mt-1 inline-flex items-center gap-1">
                <ExternalLink size={11}/> {previewUrl}
              </a>
            )}
          </div>
          <button onClick={onClose} className="p-1 text-[color:var(--aii-muted)] hover:text-red-600"><X size={16}/></button>
        </div>

        {!data ? (
          <div className="p-6 text-sm text-[color:var(--aii-muted)]">Loading…</div>
        ) : (
          <div className="p-5 space-y-3">
            <label className="block" data-testid="pb-field-slug">
              <span className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-bold">URL slug</span>
              <input value={data.slug || ""} onChange={e => set("slug", e.target.value)}
                placeholder="global-volunteer-univ"
                className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm font-mono"/>
              <span className="text-[11px] text-[color:var(--aii-muted)]">Lower-case, hyphens only. Auto-normalised on save.</span>
            </label>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block" data-testid="pb-field-logo">
                <span className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-bold">Logo URL</span>
                <input value={data.logo_url || ""} onChange={e => set("logo_url", e.target.value)}
                  placeholder="https://…/logo.png"
                  className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
              </label>
              <label className="block" data-testid="pb-field-display-name">
                <span className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-bold">Display name</span>
                <input value={data.display_name || ""} onChange={e => set("display_name", e.target.value)}
                  placeholder={partner.name}
                  className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
              </label>
              <label className="block sm:col-span-2" data-testid="pb-field-hero-line">
                <span className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-bold">Hero heading</span>
                <input value={data.hero_line || ""} onChange={e => set("hero_line", e.target.value)}
                  placeholder={`Welcome, ${partner.name}`}
                  className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
              </label>
              <label className="block" data-testid="pb-field-hero-accent">
                <span className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-bold">Hero accent word</span>
                <input value={data.hero_accent || ""} onChange={e => set("hero_accent", e.target.value)}
                  placeholder={(partner.name || "").split(" ")[0]}
                  className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
                <span className="text-[11px] text-[color:var(--aii-muted)]">Must appear as a substring of the hero heading.</span>
              </label>
              <label className="block" data-testid="pb-field-color">
                <span className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-bold">Primary colour (hex)</span>
                <input value={data.primary_color || ""} onChange={e => set("primary_color", e.target.value)}
                  placeholder="#1B7A3E"
                  className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm font-mono"/>
              </label>
              <label className="block sm:col-span-2" data-testid="pb-field-subtitle">
                <span className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-bold">Hero subtitle</span>
                <textarea value={data.hero_subtitle || ""} onChange={e => set("hero_subtitle", e.target.value)} rows={2}
                  className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
              </label>
              <label className="block" data-testid="pb-field-kicker">
                <span className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-bold">Partner card kicker</span>
                <input value={data.partner_card_kicker || ""} onChange={e => set("partner_card_kicker", e.target.value)}
                  placeholder="For Global Volunteer Univ"
                  className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
              </label>
              <label className="block" data-testid="pb-field-footer">
                <span className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-bold">Footer text</span>
                <input value={data.footer_text || ""} onChange={e => set("footer_text", e.target.value)}
                  placeholder="© Your Org 2026"
                  className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
              </label>
            </div>
          </div>
        )}

        <div className="p-4 border-t border-[color:var(--aii-border)] flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-sm px-4 py-2 border border-[color:var(--aii-border)] rounded-md hover:bg-slate-50" data-testid="pb-cancel">Cancel</button>
          <button onClick={save} disabled={saving || !data}
            className="text-sm px-4 py-2 bg-brand text-white font-semibold rounded-md hover:bg-brand-dark disabled:opacity-50 flex items-center gap-1.5" data-testid="pb-save">
            <Save size={13}/> {saving ? "Saving…" : "Save brand"}
          </button>
        </div>
      </div>
    </div>
  );
}
